import {useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {Table, Pagination, Select, Button, Tag} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import SchemeUserLeaderboardDetailModal from './SchemeUserLeaderboardDetailModal';
import UserLink from 'components/userLink/UserLink';
import {GET_LEADERBOARD_SCHEME_USERS} from '../resolvers';
import styles from './schemeUserLeaderboard.module.scss';

const {Option} = Select;

const MODES = [
  {label: 'Live', value: 'LIVE'},
  {label: 'Simulation', value: 'SIMULATION'},
];

const eligibilityOptions = [
  {label: 'All entries', value: 'ALL'},
  {label: 'Eligible', value: 'ELIGIBLE'},
  {label: 'Not eligible', value: 'INELIGIBLE'},
];

const proOptions = [
  {label: 'All users', value: 'ALL'},
  {label: 'Daba Pro', value: 'PRO'},
  {label: 'Non Pro', value: 'NON_PRO'},
];

export const SchemeUserLeaderboard = () => {
  const {id} = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [mode, setMode] = useState('LIVE');
  const [eligibilityFilter, setEligibilityFilter] = useState('ALL');
  const [proFilter, setProFilter] = useState('ALL');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {loading, data} = useQuery(GET_LEADERBOARD_SCHEME_USERS, {
    variables: {
      rewardSchemeId: id,
      mode,
      isEligibleForReward:
        eligibilityFilter === 'ALL'
          ? undefined
          : eligibilityFilter === 'ELIGIBLE',
      page,
      limit: pageSize,
    },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    onError: error => toast.error(error?.message),
  });

  const entries = data?.getAdminLeaderboardEntries?.entries || [];
  const scheme = data?.getAdminLeaderboardEntries?.rewardScheme;
  const totalEntries =
    data?.getAdminLeaderboardEntries?.pagination?.total || entries.length;
  const schemeTitle = data?.getAdminLeaderboardEntries?.rewardScheme?.title;

  const filteredEntries = useMemo(() => {
    if (proFilter === 'ALL') {
      return entries;
    }
    const wantsPro = proFilter === 'PRO';
    return entries.filter(
      entry =>
        Boolean(entry.user?.proSubscription?.hasProSubscription) === wantsPro,
    );
  }, [entries, proFilter]);

  const columns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      align: 'center',
    },
    {
      title: 'Percentage Change',
      key: 'change',
      dataIndex: 'roiChange',
      render: value => {
        const changeClass =
          value != null
            ? value < 0
              ? styles.percentNegative
              : styles.percentPositive
            : undefined;
        return <span className={changeClass}>{value}%</span>;
      },
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const user = record.user;
        const name = user
          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
          : 'Unknown user';
        return (
          <UserLink userId={user?.id} className={styles.userLink}>
            <div className={styles.userName}>{name || 'Unknown user'}</div>
            {user?.accountId && (
              <div className={styles.subText}>ID: {user.accountId}</div>
            )}
          </UserLink>
        );
      },
    },
    {
      title: 'Daba Pro',
      key: 'dabaPro',
      align: 'center',
      render: (_, record) => (
        <Tag
          color={
            record.user?.proSubscription?.hasProSubscription
              ? 'default'
              : 'black'
          }>
          {record.user?.proSubscription?.hasProSubscription ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Total Trades',
      key: 'totalTrades',
      render: (_, record) => {
        const details = record.eligibilityDetails;
        const completed = details?.numberOfTransactions ?? 0;
        const required = details?.requiredNumberOfTransactions ?? 0;
        if (!details) {
          return '-';
        }
        return `${completed}/${required}`;
      },
    },
    {
      title: 'Wins (YTD)',
      key: 'wins',
      align: 'center',
      render: (_, record) =>
        `${record.eligibilityDetails?.YTDWins ?? '0'} / ${
          record.eligibilityDetails?.maxNumberOfRewards
        }`,
    },
    {
      title: 'Eligibility',
      key: 'eligibility',
      align: 'center',
      render: (_, record) => (
        <Tag color={record.isEligibleForReward ? 'green' : 'red'}>
          {record.isEligibleForReward ? 'Eligible' : 'Not Eligible'}
        </Tag>
      ),
    },
    {
      title: 'Reward Status',
      key: 'rewardStatus',
      align: 'center',
      render: (_, record) => (
        <Tag
          color={
            {
              'PENDING': 'yellow',
              'APPROVED': 'green',
              'REJECTED': 'red',
              'FAILED': 'volcano',
              'EXPIRED': 'grey',
            }[record.reward?.status] || 'default'
          }>
          {record.reward?.status}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Button
          type="default"
          className={styles.viewButton}
          onClick={() => handleViewRow(record)}>
          View
        </Button>
      ),
    },
  ];

  const handleViewRow = entry => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEntry(null);
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <div className={styles.title}>{schemeTitle} Leaderboard</div>
          {entries[0]?.cycleStartDate && entries[0]?.cycleEndDate && (
            <div className={styles.subtitle}>
              {`${moment(entries[0].cycleStartDate).format(
                'MMM D, YYYY',
              )} - ${moment(entries[0].cycleEndDate).format('MMM D, YYYY')}`}
            </div>
          )}
        </div>
      </header>
      <div className={styles.filterRow}>
        <Select
          value={mode}
          disabled
          onChange={value => {
            setMode(value);
            setPage(1);
          }}
          className={styles.select}
          placeholder="Mode">
          {MODES.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        <Select
          value={eligibilityFilter}
          onChange={value => {
            setEligibilityFilter(value);
            setPage(1);
          }}
          className={styles.select}
          placeholder="Eligibility">
          {eligibilityOptions.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        <Select
          value={proFilter}
          onChange={value => setProFilter(value)}
          className={styles.select}
          placeholder="Pro status">
          {proOptions.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>
      <Table
        className={styles.table}
        rowKey="id"
        columns={columns}
        loading={loading}
        dataSource={filteredEntries}
        pagination={false}
        locale={{emptyText: 'No user entries found'}}
        size="middle"
      />
      <div className={styles.pagination}>
        <Pagination
          size="small"
          current={page}
          pageSize={pageSize}
          total={totalEntries}
          showSizeChanger
          onChange={(nextPage, size) => {
            setPage(nextPage);
            if (size !== pageSize) {
              setPageSize(size);
            }
          }}
        />
      </div>
      <SchemeUserLeaderboardDetailModal
        entry={selectedEntry}
        visible={isModalOpen}
        onClose={closeModal}
        scheme={scheme}
      />
    </div>
  );
};
